import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Authentication & Registration
import AuthForms from '../arca_Auth/AuthForms';
import PasswordReset from '../arca_Auth/PasswordReset';
import PasswordSetup from '../arca_Auth/PasswordSetup';
import ForceChangePassword from '../arca_Auth/ForceChangePassword';
import MainReg from '../arca_Mem/MainReg';
import SetPasswordForm from '../arca_Mem/SetPasswordForm';

// Main Dashboards
import UserDashboard from '../components/UserDashboard';
import AdminDashboard from '../components/AdminDashboard';
import CountryDirectorsDashboard from '../components/CountryDirectorsDashboard';

// Public Website Pages
import HomePage from '../pages/HomePage';
import AboutUsPage from '../pages/AboutUsPage';
import DonatePage from '../pages/DonatePage';
import VolunteerPage from '../pages/VolunteerPage';
import PresidentMessagePage from '../pages/PresidentMessagePage';
import YouTubePage from '../pages/YouTubePage';
import AdvocacyAwarenessPage from '../pages/AdvocacyAwarenessPage';
import ResearchInnovationPage from '../pages/ResearchInnovationPage';
import EquipmentResourcesPage from '../pages/EquipmentResourcesPage';
import ResourcesHubPage from '../pages/ResourcesHubPage';
import OurStory from '../components/public/about/OurStory';
import MissionVision from '../components/public/about/MissionVision';
import Leadership from '../components/public/about/Leadership';
import PartnersAndCollaborators from '../components/public/about/PartnersAndCollaborators';
import ChaptersPage from '../pages/ChaptersPage';
import InstitutionsPage from '../components/public/InstitutionsPage';
import EducationTraining from '../components/public/programs/EducationTraining';
import NewsPage from '../components/public/NewsPage';
import NewsDetailPage from '../components/public/NewsDetailPage';
import ContactPage from '../pages/ContactPage';
import PhotoGallery from '../components/public/media/PhotoGallery';
import PlaceholderPage from '../components/public/PlaceholderPage';
import PublicationsPage from '../pages/resources/PublicationsPage';
import TrainingMaterialsPage from '../pages/resources/TrainingMaterialsPage';
import FAQsPage from '../pages/resources/FAQsPage';
import PolicyDocumentsPage from '../pages/resources/PolicyDocumentsPage';
import PolicyDevelopmentsPage from '../pages/resources/PolicyDevelopmentsPage';
import ClinicalGuidelinesPage from '../pages/resources/ClinicalGuidelinesPage';
import PressReleasesPage from '../pages/media/PressReleasesPage';
import MediaKitPage from '../pages/media/MediaKitPage';
import SuccessStoriesPage from '../pages/impact/SuccessStoriesPage';
import AnnualReportsPage from '../pages/impact/AnnualReportsPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import TermsConditionsPage from '../pages/TermsConditionsPage';
import Arca2027SurveyPage from '../pages/Arca2027SurveyPage';
import GenericSurveyPage from '../pages/GenericSurveyPage';
import SurveysListPage from '../pages/SurveysListPage';

// Store Components
import StorePage from '../components/Store/StorePage';
import ProductDetails from '../components/Store/ProductDetails';
import ShoppingCart from '../components/Store/ShoppingCart';

// Vendor
import VendorRegistrationPage from '../pages/VendorRegistrationPage';
import Checkout from '../components/Store/Checkout';
import OrderSuccess from '../components/Store/OrderSuccess';

// Virtual University Component
import VirtualUniversityPage from '../components/VirtualUniversityPage';

// User Profile
import ProfilePage from '../components/ProfilePage';
import MemberDashboard from '../components/MemberDashboard';
import Messages from '../components/Messages';
import PurchasesAndCart from '../components/PurchasesAndCart';
import MembershipPaymentPage from '../components/MembershipPaymentPage';
import EventPaymentPage from '../components/EventPaymentPage';
import CoursePaymentPage from '../components/CoursePaymentPage';

// Events Components
import EventsPage from '../pages/EventsPage';
import EventDetailsPage from '../pages/EventDetailsPage';
import EventRegistrationPage from '../pages/EventRegistrationPage';
import CpdRegistrationPage from '../pages/CpdRegistrationPage';

// Community Component
import CommunityDashboard from '../components/Community/CommunityDashboard';
import TopicView from '../components/Community/components/TopicView';

// Placeholder Components
import {
  LMSPage,
  CoursePage,
  CoursePlayer,
  CommunityPage,
  ForumPage,
  MessagingPage,
  CPDPage,
  CPDTracker,
  ResourcesPage,
  KnowledgeHub,
  MembershipPage,
  PaymentsPage,
  SettingsPage,
  AdminMembers,
  AdminEvents,
  AdminStore,
  AdminCourses,
  AdminReports,
  AdminSettings
} from '../components/PlaceholderComponents';

// Protected Route Component
import ProtectedRoute from '../components/ProtectedRoute';

const AppRoutes = ({ user, handleAuthSuccess, handleLogout }) => {
  return (
    <Routes>
      {/* Public Routes - Always Accessible */}
      <Route path="/" element={<HomePage />} />
      <Route path="/home" element={<HomePage />} />

      {/* Events Routes - Publicly Accessible */}
      <Route path="/events" element={<EventsPage user={user} />} />
      <Route path="/events/:eventId" element={<EventDetailsPage user={user} />} />
      <Route path="/events/:eventId/register" element={<EventRegistrationPage user={user} />} />
      <Route path="/cpd-registration" element={<CpdRegistrationPage />} />

      {/* Dashboard Redirect for Logged-in Users */}
      <Route path="/my-dashboard" element={
        user ? (
          user.userType === 'admin' || user.userType === 'super_admin' ?
            <Navigate to="/admin" replace /> :
            user.userType === 'country_director' ?
              <Navigate to="/director" replace /> :
              <Navigate to="/dashboard" replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />

      {/* About Us Routes */}
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/about/our-story" element={<OurStory />} />
      <Route path="/about/mission-vision" element={<MissionVision />} />
      <Route path="/about/leadership" element={<Leadership />} />
      <Route path="/about/president-message" element={<PresidentMessagePage />} />
      <Route path="/about/partners" element={<PartnersAndCollaborators />} />

      {/* Programs Routes */}
      <Route path="/programs/education-training" element={<EducationTraining />} />
      <Route path="/programs/research-innovation" element={<ResearchInnovationPage />} />
      <Route path="/programs/advocacy-awareness" element={<AdvocacyAwarenessPage />} />
      <Route path="/programs/equipment-support" element={<EquipmentResourcesPage />} />

      {/* Impact Routes */}
      <Route path="/impact/success-stories" element={<SuccessStoriesPage />} />
      <Route path="/impact/annual-reports" element={<AnnualReportsPage />} />

      {/* Resources Routes */}
      <Route path="/resources" element={<ResourcesHubPage />} />
      <Route path="/resources/publications" element={<PublicationsPage />} />
      <Route path="/resources/training-materials" element={<TrainingMaterialsPage />} />
      <Route path="/resources/policy-documents" element={<PolicyDocumentsPage />} />
      <Route path="/resources/policy-developments" element={<PolicyDevelopmentsPage />} />
      <Route path="/resources/clinical-guidelines" element={<ClinicalGuidelinesPage />} />
      <Route path="/resources/faqs" element={<FAQsPage />} />

      {/* Media Routes */}
      <Route path="/media/youtube-videos" element={<YouTubePage />} />
      <Route path="/media/photo-gallery" element={<PhotoGallery />} />
      <Route path="/media/press-releases" element={<PressReleasesPage />} />
      <Route path="/media/media-kit" element={<MediaKitPage />} />

      {/* Chapters Route */}
      <Route path="/chapters" element={<ChaptersPage />} />

      {/* Institutions Route */}
      <Route path="/institutions" element={<InstitutionsPage />} />

      {/* News Route */}
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:id" element={<NewsDetailPage />} />

      {/* Contact Route */}
      <Route path="/contact" element={<ContactPage />} />

      {/* ARCA 2027 Survey */}
      <Route path="/conference/arca-2027-survey" element={<Arca2027SurveyPage />} />

      {/* Generic Survey Pages */}
      <Route path="/surveys" element={<SurveysListPage />} />
      <Route path="/survey/:slug" element={<GenericSurveyPage />} />

      {/* Privacy Policy Route */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

      {/* Terms & Conditions Route */}
      <Route path="/terms-conditions" element={<TermsConditionsPage />} />

      {/* Get Involved Routes */}
      <Route path="/get-involved/become-member" element={<Navigate to="/membership_registration" replace />} />
      <Route path="/get-involved/volunteer" element={<VolunteerPage />} />
      <Route path="/get-involved/vendor-membership" element={<VendorRegistrationPage />} />
      <Route path="/vendor-registration" element={<VendorRegistrationPage />} />
      <Route path="/get-involved/partner" element={<ContactPage />} />
      <Route path="/get-involved/donate" element={<DonatePage />} />

      {/* Authentication Routes */}
      <Route path="/login" element={
        user ? (
          user.userType === 'admin' || user.userType === 'super_admin' ?
            <Navigate to="/admin" replace /> :
            user.userType === 'country_director' ?
              <Navigate to="/director" replace /> :
              <Navigate to="/dashboard" replace />
        ) : (
          <AuthForms onAuthSuccess={handleAuthSuccess} />
        )
      } />
      <Route path="/signin" element={
        user ? (
          user.userType === 'admin' || user.userType === 'super_admin' ?
            <Navigate to="/admin" replace /> :
            user.userType === 'country_director' ?
              <Navigate to="/director" replace /> :
              <Navigate to="/dashboard" replace />
        ) : (
          <AuthForms onAuthSuccess={handleAuthSuccess} />
        )
      } />

      {/* Registration Routes */}
      <Route path="/membership_registration" element={<MainReg />} />
      <Route path="/set-password" element={<SetPasswordForm />} />
      <Route path="/setup-password" element={<PasswordSetup />} />
      <Route path="/reset-password" element={<PasswordReset />} />

      {/* User Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MemberDashboard user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Force Change Password (after admin reset) */}
      <Route path="/force-change-password" element={
        <ProtectedRoute>
          <ForceChangePassword />
        </ProtectedRoute>
      } />

      {/* Messages Route */}
      <Route path="/dashboard/messages" element={
        <ProtectedRoute>
          <Messages user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Purchases and Cart */}
      <Route path="/purchases" element={
        <ProtectedRoute>
          <PurchasesAndCart user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/admin/members" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="members" />
        </ProtectedRoute>
      } />
      <Route path="/admin/store" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="products" />
        </ProtectedRoute>
      } />
      <Route path="/admin/courses" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="courses" />
        </ProtectedRoute>
      } />
      <Route path="/admin/events" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="events" />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="reports" />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="settings" />
        </ProtectedRoute>
      } />
      <Route path="/admin/vendors" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="vendors" />
        </ProtectedRoute>
      } />
      <Route path="/admin/survey" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="surveys" />
        </ProtectedRoute>
      } />
      <Route path="/admin/cpd-registrations" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard user={user} onLogout={handleLogout} defaultTab="cpd-registrations" />
        </ProtectedRoute>
      } />

      {/* Director Routes */}
      <Route path="/director" element={
        <ProtectedRoute requiredRole="country_director">
          <CountryDirectorsDashboard user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/director/members" element={
        <ProtectedRoute requiredRole="country_director">
          <CountryDirectorsDashboard user={user} onLogout={handleLogout} defaultTab="members" />
        </ProtectedRoute>
      } />
      <Route path="/director/reports" element={
        <ProtectedRoute requiredRole="country_director">
          <CountryDirectorsDashboard user={user} onLogout={handleLogout} defaultTab="reports" />
        </ProtectedRoute>
      } />
      <Route path="/director/settings" element={
        <ProtectedRoute requiredRole="country_director">
          <CountryDirectorsDashboard user={user} onLogout={handleLogout} defaultTab="settings" />
        </ProtectedRoute>
      } />

      {/* LMS/Courses Routes - Publicly Accessible */}
      <Route path="/university" element={<VirtualUniversityPage user={user} onLogout={handleLogout} />} />
      <Route path="/courses" element={<VirtualUniversityPage user={user} onLogout={handleLogout} />} />
      <Route path="/courses/:courseId" element={<CoursePage user={user} onLogout={handleLogout} />} />
      <Route path="/courses/:courseId/lesson/:lessonId" element={
        <ProtectedRoute>
          <CoursePlayer user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Store Routes - Publicly Accessible */}
      <Route path="/store" element={<StorePage user={user} onLogout={handleLogout} />} />
      <Route path="/store/product/:productId" element={<ProductDetails user={user} onLogout={handleLogout} />} />
      <Route path="/cart" element={<ShoppingCart user={user} onLogout={handleLogout} />} />
      <Route path="/checkout" element={
        <ProtectedRoute>
          <Checkout user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/order-success" element={
        <ProtectedRoute>
          <OrderSuccess user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Community Routes - Public View, Login Required for Interaction */}
      <Route path="/community" element={<CommunityDashboard user={user} onLogout={handleLogout} />} />
      <Route path="/community/profiles" element={<CommunityDashboard user={user} onLogout={handleLogout} />} />
      <Route path="/community/forums" element={<CommunityDashboard user={user} onLogout={handleLogout} />} />
      <Route path="/community/messages" element={
        <ProtectedRoute>
          <CommunityDashboard user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/community/events" element={<CommunityDashboard user={user} onLogout={handleLogout} />} />
      <Route path="/community/resources" element={<CommunityDashboard user={user} onLogout={handleLogout} />} />
      <Route path="/community/announcements" element={<CommunityDashboard user={user} onLogout={handleLogout} />} />
      <Route path="/community/topic/:topicId" element={<TopicView user={user} onLogout={handleLogout} />} />

      {/* CPD Routes */}
      <Route path="/cpd" element={
        <ProtectedRoute>
          <CPDPage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/cpd/tracker" element={
        <ProtectedRoute>
          <CPDTracker user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Resources Routes */}
      <Route path="/resources" element={
        <ProtectedRoute>
          <ResourcesPage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/knowledge-hub" element={
        <ProtectedRoute>
          <KnowledgeHub user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* User Management Routes */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/membership" element={
        <ProtectedRoute>
          <MembershipPage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/payments" element={
        <ProtectedRoute>
          <MembershipPaymentPage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />
      <Route path="/event-payment" element={
        <EventPaymentPage user={user} onLogout={handleLogout} />
      } />
      <Route path="/course-payment" element={
        <CoursePaymentPage user={user} onLogout={handleLogout} />
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage user={user} onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Catch all route */}
      <Route path="*" element={
        <Navigate to={
          user ? (
            user.userType === 'admin' || user.userType === 'super_admin' ? '/admin' :
              user.userType === 'country_director' ? '/director' :
                '/dashboard'
          ) : '/login'
        } replace />
      } />
    </Routes>
  );
};

export default AppRoutes;